import * as Toast from "@radix-ui/react-toast";
import classNames from "classnames";
import React from "react";
import KuikaButton from "../kuika-button/kuika-button";
import KuikaCloseButton from "../kuika-close-button/kuika-close-button";
import KuikaCopyButton from "../kuika-copy-button/kuika-copy-button";
import KuikaSmallSpinner from "../kuika-small-spinner/kuika-small-spinner";
import KuikaSvg from "../kuika-svg/kuika-svg";
import KuikaSwap from "../kuika-swap/kuika-swap";
import KuikaH6 from "../typography/H6/H6";
import KuikaSmallText from "../typography/SmallText/SmallText";
import { IToastAction, ToastType } from "./toast-service";
import "./kuika-toast.scss";

export type { ToastType } from "./toast-service";

export interface KuikaToastProps {
  type: ToastType;
  title?: string;
  message: string;
  onClose?: () => void;
  /**
   * A button under the message — the Cancel of a promise toast. Clicking it runs `action.onClick`
   * and closes the toast (a Radix `Toast.Action`, so assistive tech is told `label` as its
   * alternative).
   */
  action?: IToastAction;
}

/** Titled toast types; a `neutral` toast is one line with no title and no icon. */
type TitledToastType = Exclude<ToastType, "neutral">;

const DEFAULT_TITLES: Record<TitledToastType, string> = {
  error: "Something went wrong",
  success: "Success!",
  warning: "Warning!",
  info: "Info",
  loading: "Loading"
};

const ICON_NAMES: Record<Exclude<TitledToastType, "loading">, string> = {
  error: "x",
  success: "check",
  warning: "info-circle",
  info: "info-circle"
};

/**
 * Keeps a pointer-down on the toast's text from reaching Radix's swipe-to-dismiss, so the text can be
 * selected and copied.
 */
const stopPointerDown = (event: React.PointerEvent<HTMLElement>): void => {
  event.stopPropagation();
};

interface IToastActionButtonProps {
  action: IToastAction;
}

/** A Radix `Toast.Action`, so assistive tech is told `label` as its alternative. */
const ToastActionButton = ({ action }: IToastActionButtonProps): JSX.Element => (
  <Toast.Action asChild altText={action.label}>
    <KuikaButton type="secondary" color="neutral" size="sm" onClick={action.onClick} data-testid={action.testId}>
      {action.label}
    </KuikaButton>
  </Toast.Action>
);

interface INeutralToastProps {
  message: string;
  onClose?: () => void;
  action?: IToastAction;
}

/** One line: the message, its button (Undo), the close button. */
const NeutralToast = ({ message, onClose, action }: INeutralToastProps): JSX.Element => (
  <div className="kuika-toast kuika-toast--neutral" data-testid="kuikaToast-neutral">
    <div className="kuika-toast__row">
      <KuikaSmallText className="kuika-toast__message kuika-toast__message--inline" onPointerDown={stopPointerDown}>
        {message}
      </KuikaSmallText>
      {action !== undefined && <ToastActionButton action={action} />}
      <Toast.Close asChild>
        <KuikaCloseButton size="sm" onClick={onClose} className="kuika-toast__close-button" />
      </Toast.Close>
    </div>
  </div>
);

/**
 * One toast card. When the same card changes type (a loading toast settling), its icon and title
 * swap in place instead of cutting.
 */
const KuikaToast: React.FC<KuikaToastProps> = ({ type, title, message, onClose, action }) => {
  if (type === "neutral") {
    return <NeutralToast message={message} onClose={onClose} action={action} />;
  }

  const displayTitle: string = title || DEFAULT_TITLES[type];

  const toastClassName: string = classNames("kuika-toast", `kuika-toast--${type}`);

  return (
    <div className={toastClassName} data-testid={`kuikaToast-${type}`}>
      <div className="kuika-toast__header">
        <div className="kuika-toast__header-left">
          <KuikaSwap contentKey={type} variant="blur" className="kuika-toast__icon">
            {type === "loading" ? (
              <KuikaSmallSpinner className="kuika-toast__spinner" />
            ) : (
              <div className={classNames("kuika-toast__icon-circle", `kuika-toast__icon-circle--${type}`)}>
                <KuikaSvg name={ICON_NAMES[type]} width={12} height={12} color="var(--white)" />
              </div>
            )}
          </KuikaSwap>
          <KuikaH6 className="kuika-toast__title" onPointerDown={stopPointerDown}>
            <KuikaSwap contentKey={displayTitle} variant="blur">
              {displayTitle}
            </KuikaSwap>
          </KuikaH6>
        </div>
        <div className="kuika-toast__actions">
          {/* An error's text is what gets pasted into a support ticket: its glyph rolls to a check once
              the message is on the clipboard, then back. */}
          {type === "error" && (
            <KuikaCopyButton value={message} size="sm" label="Copy message" className="kuika-toast__copy-button" />
          )}
          <Toast.Close asChild>
            <KuikaCloseButton size="sm" onClick={onClose} className="kuika-toast__close-button" />
          </Toast.Close>
        </div>
      </div>
      <KuikaSmallText className="kuika-toast__message" onPointerDown={stopPointerDown}>
        {message}
      </KuikaSmallText>
      {action !== undefined && (
        <div className="kuika-toast__footer">
          <ToastActionButton action={action} />
        </div>
      )}
    </div>
  );
};

export default KuikaToast;
